import { z } from "zod";
import { MAX_TRIAL_DURATION_DAYS, MIN_TRIAL_DURATION_DAYS } from "@atsready/shared";

/**
 * The trial-policy form's schema, and the **commit gate only**.
 *
 * It is deliberately not what renders the refusal messages. React Hook Form populates
 * `formState.errors` on submit unless `mode` says otherwise, and none of this package's
 * fourteen `useForm` call sites sets `mode` — so a schema-driven message appears only after
 * the operator presses Save, while the card's live preview updates on every keystroke. The
 * approved design shows the message the typed value earns, as it is typed. So the card
 * renders messages from `judgeTrialDays` directly and uses this schema to decide whether the
 * commit may fire, which makes the two structurally incapable of disagreeing rather than
 * merely intended to agree.
 *
 * `coercedNumber` before the integer check, because an `<input type="number">` hands back a
 * string and `z.number()` on a string is a type error rather than a validation failure. The
 * pipe is why the form needs the three-generic `useForm<z.input<...>, unknown, Output>` shape
 * this package already uses at `RateLimitsDialog.tsx` — `@hookform/resolvers` v5 cannot infer
 * input and output from a piped schema.
 */
const coercedDays = z
  .string()
  .or(z.number())
  .transform((value) => (typeof value === "number" ? value : value.trim()))
  .transform((value) => (value === "" ? Number.NaN : Number(value)));

export const trialPolicySchema = z.object({
  durationDays: coercedDays.pipe(
    z
      .number()
      .int()
      // The bounds come from `@atsready/shared`, never from literals here. Two Zod schemas
      // and two approved copy strings state these numbers, and the constants are the only
      // thing keeping the four in step — the copy strings cannot be templated, because gate
      // 1 froze them, so they are pinned to the constants by test instead.
      .min(MIN_TRIAL_DURATION_DAYS)
      .max(MAX_TRIAL_DURATION_DAYS),
  ),
  // `.trim()` BEFORE `.min(1)`, so a reason of only spaces is refused rather than stored.
  // The server does the same. A client that checked `.min(1)` without trimming would send
  // whitespace, and the operator would meet a bare validation failure instead of the message
  // written for exactly that case.
  reason: z.string().trim().min(1).max(500),
});

export type TrialPolicyFormInput = z.input<typeof trialPolicySchema>;
export type TrialPolicyFormOutput = z.output<typeof trialPolicySchema>;
